// src/features/locker-controllers/components/LockerControllerCreate.tsx
import React from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  NumberInput,
  ReferenceInput,
  SelectInput,
  required,
  useNotify,
  useRedirect,
  useGetIdentity,
  useTranslate,
  FormDataConsumer,
} from 'react-admin';
import { 
  Box,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { 
  Router as RouterIcon,
} from '@mui/icons-material';

import { MyToolbar } from '../../../components/forms/MyToolbar';
import { MyFormContainer } from '../../../components/forms/MyFormContainer';
import {  
  MyFormSection, 
  CenteredFormLayout
} from '../../../components/forms/MyFormLayout';

import { useLockerControllerValidation } from '../hooks/useLockerControllerValidation';
import { useLockerControllerPermissions } from '../hooks/useLockerControllerPermissions';
import { prepareLockerControllerForAPI, LockerControllerFormData } from '../types';

const LockerControllerCreate: React.FC = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { data: identity } = useGetIdentity();
  const { canCreateLockerControllers } = useLockerControllerPermissions();
  
  const {
    validateName,
    validateIpAddress,
    validateTcpPort,
    validateHotelId,
    validateZoneId,
  } = useLockerControllerValidation();

  // Verificar permisos
  if (!canCreateLockerControllers) {
    return (
      <MyFormContainer maxWidth="md">
        <Alert severity="error">
          {translate('resources.locker-controllers.messages.no_permission_create')}
        </Alert>
      </MyFormContainer>
    );
  }

  const transform = (data: LockerControllerFormData) => {
    return prepareLockerControllerForAPI(data);
  };

  const handleSuccess = () => {
    notify(translate('resources.locker-controllers.messages.creation_success'), { type: 'success' });
    redirect('list', 'locker-controllers');
  };

  const handleCancel = () => redirect('list', 'locker-controllers');

  return (
    <Create
      title={translate('resources.locker-controllers.titles.create')}
      transform={transform}
      mutationOptions={{ onSuccess: handleSuccess }}
      redirect={false}
    >
      <MyFormContainer>
        <SimpleForm toolbar={<MyToolbar onCancel={handleCancel} />}>
          <CenteredFormLayout>
              <MyFormSection
                title="Información General"
                subtitle="Datos básicos del controlador"
                icon={<RouterIcon />}
                variant="outlined"
              >
                <TextInput
                  source="name"
                   size='small'
                  label="Nombre del Controlador"
                  validate={[required(), validateName]}
                  fullWidth
                
                />

                <Box display="flex" gap={2} flexDirection={isMobile ? 'column' : 'row'}>
                  <TextInput
                   size='small'
                    source="ipAddress"
                    label="Dirección IP"
                    validate={[required(), validateIpAddress]}
                    fullWidth
                   
                    placeholder="192.168.1.100"
                  />

                  <NumberInput
                   size='small'
                    source="tcpPort"
                    label="Puerto TCP"
                    validate={[required(), validateTcpPort]}
                    fullWidth
                    min={1}
                    max={65535}
                  
                    defaultValue={8080}
                  />
                </Box>
           
                <Box display="flex" gap={2} flexDirection={isMobile ? 'column' : 'row'}>
                  <ReferenceInput
                    source="hotelId"
                    reference="hotels"
                    label="Hotel"
                    sort={{ field: 'name', order: 'ASC' }}
                    filter={identity?.role !== 'SuperAdmin' ? { id: identity?.hotelId } : {}}
                  >
                    <SelectInput 
                      size='small'
                      optionText="name" 
                      fullWidth
                      disabled={identity?.role !== 'SuperAdmin'}
                      defaultValue={identity?.hotelId}
                      validate={[required(), validateHotelId]}
                    />
                  </ReferenceInput>

                
                  <FormDataConsumer>
                    {({ formData }) => (
                      <ReferenceInput
                        source="zoneId"
                        reference="zones"
                        label="Zona (Opcional)"
                        sort={{ field: 'name', order: 'ASC' }}
                        filter={{ hotelId: formData.hotelId || identity?.hotelId }} 
                        key={formData.hotelId}
                      >
                        <SelectInput 
                          size='small'
                          optionText="name" 
                          fullWidth
                          validate={validateZoneId}
                          disabled={!formData.hotelId && !identity?.hotelId} 
                          helperText={(!formData.hotelId && !identity?.hotelId) ? "Seleccione primero un hotel" : undefined}
                        />
                      </ReferenceInput>
                    )}
                  </FormDataConsumer>
                </Box>
            </MyFormSection>
          </CenteredFormLayout>
        </SimpleForm>
      </MyFormContainer>
    </Create>
  );
};

export default LockerControllerCreate;